import { useState } from "react";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { downloadFile, API } from "../lib/api";

export const MANAGERS = {
  bitwarden: {
    name: "Bitwarden", file: "hearthvault-bitwarden.json", path: "/passwords/export/bitwarden.json", help: "https://bitwarden.com/help/import-data/",
    blurb: "Bitwarden is open source, free on every plan and has a documented import format — so your vault's logins can live in a real password manager with autofill, while HearthVault keeps the estate picture.",
    steps: [
      "Download the export below — it's Bitwarden's own import format (unencrypted JSON), so keep it only as long as you need it.",
      "In the Bitwarden web vault go to Tools → Import data, choose “Bitwarden (json)”, pick the file and import. Your logins land in a “HearthVault” folder.",
      "Delete the downloaded file. Then set up Bitwarden Emergency Access (Settings → Emergency access) so a trusted person can inherit your vault — and record it in your Emergency kit card here.",
    ],
  },
  "1password": {
    name: "1Password", file: "hearthvault-1password.csv", path: "/passwords/export/1password.csv", help: "https://support.1password.com/import/",
    blurb: "1Password imports a simple CSV (Title, Website, Username, Password, Notes). Your logins land as Login items you can autofill everywhere, while HearthVault keeps the estate picture.",
    steps: [
      "Download the CSV below — it's unencrypted, so keep it only as long as you need it.",
      "In 1Password.com go to your account menu → Import → choose “Other / CSV”, pick the vault to import into and upload the file. Check the column mapping (Title, Website, Username, Password, Notes) and import.",
      "Delete the downloaded file. Then print 1Password's Emergency Kit (Secret Key + account password) and record where it lives in your Emergency kit card here.",
    ],
  },
};

export const PasswordExportDialog = ({ manager, onClose }) => {
  const [busy, setBusy] = useState(false);
  const m = MANAGERS[manager] || MANAGERS.bitwarden;
  const download = async () => {
    setBusy(true);
    try { await downloadFile(`${API}${m.path}`, m.file); toast.success(`Export downloaded — import it in ${m.name}, then delete the file`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't export"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={Boolean(manager)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${manager || "bitwarden"}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Send to {m.name}</DialogTitle>
          <DialogDescription>{m.blurb} Owner only.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          {m.steps.map((s, i) => <li key={s} className="flex gap-3"><span className="h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-mono">{i + 1}</span><span className="text-muted-foreground leading-relaxed">{s}</span></li>)}
        </ol>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid={`${manager || "bitwarden"}-download-btn`} disabled={busy} onClick={download} className="rounded-full gap-1.5"><Download className="h-4 w-4" /> {busy ? "Preparing…" : `Download ${m.name} ${manager === "1password" ? "CSV" : "JSON"}`}</Button>
          <a href={m.help} target="_blank" rel="noreferrer" data-testid={`${manager || "bitwarden"}-help-link`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline px-2"><ExternalLink className="h-3.5 w-3.5" /> {m.name} import guide</a>
        </div>
      </DialogContent>
    </Dialog>
  );
};
